"use client";

import React, { useState, useEffect, useRef } from "react";
import { Box, Typography, useMediaQuery, Tooltip } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ConfirmDialog from "@/app/components/modals/ConfirmDialog";
import { useToast } from "@/utils/theme/useToast";
import dynamic from "next/dynamic";

const StripePaymentForm = dynamic(() => import("./StripePaymentForm.jsx"), {
  loading: () => (
    <Box
      sx={{
        textAlign: "center",
        padding: "20px",
        minHeight: "70px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Typography variant="body1" color="textSecondary">
        Loading payment form...
      </Typography>
    </Box>
  ),
  ssr: false,
});

const AddPaymentModal = ({
  open,
  handleClose,
  isInstantBooking = false,
  requestId = null,
  onPaymentSuccess = null,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const toast = useToast();
  const [canClose, setCanClose] = useState(false);
  const [isCreditCardLoading, setIsCreditCardLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const firstInputRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setCanClose(true);
    }, 3000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (open && firstInputRef.current) {
      firstInputRef.current.focus();
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      setErrorMessage("");
      setIsCreditCardLoading(false);
    }
  }, [open]);

  const enhancedHandleClose = () => {
    if (canClose) {
      handleClose();
      if (!isInstantBooking) {
        const newUrl = window.location.origin + window.location.pathname;
        window.history.replaceState({}, "", newUrl);
      }
    }
  };

  const handlePaymentSuccess = () => {
    if (isInstantBooking) {
      // toast.show(
      //   "Payment Method Added",
      //   "Your payment method has been verified and your booking is confirmed."
      // );
    } else {
      toast.show(
        "Payment Method Added",
        "Your payment method has been successfully added and verified.",
      );
    }

    if (isInstantBooking && onPaymentSuccess) {
      onPaymentSuccess();
    } else {
      enhancedHandleClose();
    }
  };

  const getDialogTitle = () => {
    return isInstantBooking ? "Complete Your Booking" : "Add Your Card";
  };

  const getDialogMessage = () => {
    if (isInstantBooking) {
      return "Add your payment method to instantly secure your meeting. Once verified, it will be scheduled automatically.";
    }
    return "Add your payment method to schedule your meeting. Your details are encrypted and securely processed by Stripe.";
  };

  const dialogBody = (
    <Box>
      <Typography
        variant="body1"
        sx={{ fontSize: "0.9375rem", lineHeight: 1.65, mb: 3 }}
      >
        {getDialogMessage()}
      </Typography>

      <Box
        sx={{
          minHeight: "70px",
          transition: "none",
        }}
      >
        <StripePaymentForm
          isPayment={true}
          setErrorMessage={setErrorMessage}
          setIsCreditCardLoading={setIsCreditCardLoading}
          isCreditCardLoading={isCreditCardLoading}
          handleClose={handlePaymentSuccess}
          inputRef={firstInputRef}
          isInstantBooking={isInstantBooking}
          requestId={requestId}
        />
      </Box>

      <Box sx={{ mt: 2 }}>
        {errorMessage ? (
          <Typography color="error" variant="body2">
            {errorMessage}
          </Typography>
        ) : (
          <Tooltip title="We use 256-bit encryption and PCI compliance to keep your payment information safe">
            <Typography variant="body2" color="textSecondary">
              All transactions are secure and encrypted.
            </Typography>
          </Tooltip>
        )}
      </Box>
    </Box>
  );

  return (
    <ConfirmDialog
      open={open}
      onClose={enhancedHandleClose}
      title={getDialogTitle()}
      body={dialogBody}
      showConfirmButton={false}
      showIgnoreButton={false}
      showCloseIcon={true}
      disableBackdropClick={!canClose}
      disableEscapeKeyDown={!canClose}
      modalStyle="center"
      backdropStyle="frosted"
      customStyle={{
        width: "min(620px, calc(100vw - 48px))",
        px: 0,
        borderRadius: "10px",
        maxHeight: "calc(100dvh - 48px)",
        overflowY: "auto",
      }}
      contentSx={{ px: 4, pt: 4, pb: 3 }}
      titleSx={{
        fontSize: "1.625rem",
        lineHeight: 1.25,
        letterSpacing: "-0.5px",
      }}
    />
  );
};

export default AddPaymentModal;
